import { Injectable } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { AppConfigService } from './app-config.service';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class AppInsightsService {
  private appInsights: ApplicationInsights | undefined;
  private configuredSeverityLevel: number = SeverityLevel.Information;

  constructor(private readonly appConfigService: AppConfigService,
    private readonly router: Router) { }

  async init() {
    const angularPlugin = new AngularPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: this.appConfigService.config?.connectionString,
        enableAutoRouteTracking: true,
        enableResponseHeaderTracking: true,
        extensions: [<any>angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router, errorServices: [new ErrorHandlerService(this)] }
        }
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView();
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags = envelope.tags || [];
      envelope.tags['ai.cloud.role'] = 'GenesysCommUI';
    });
    this.configuredSeverityLevel = this.getConfiguredSeverity();
  }

   private getConfiguredSeverity(): number {
    const levelStr = this.appConfigService.config?.LogLevel;
    return SeverityLevel[levelStr as keyof typeof SeverityLevel] ?? SeverityLevel.Information;
  }

  logPageView(name?: string, url?: string) {
    this.appInsights?.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }, severityLevel: number = SeverityLevel.Information) {
    if (severityLevel >= this.configuredSeverityLevel) {
    this.appInsights?.trackEvent({ name: name }, properties);
    }
  }

  logException(exception: Error, severityLevel: number = SeverityLevel.Error) {
    if (severityLevel >= this.configuredSeverityLevel) {
      this.appInsights?.trackException({ exception: exception, severityLevel: severityLevel });
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }, severityLevel: number = SeverityLevel.Information) {
    if (severityLevel >= this.configuredSeverityLevel) {
      this.appInsights?.trackTrace({ message: message, severityLevel: severityLevel }, properties);
    }
  }
}
